import { computed, getCurrentInstance, ref } from "@vue/composition-api";
import {
  Announcement,
  AnnouncementTypeEnum,
  InputAnnouncementSearch,
} from "@scrile/api-provider/dist/api/AnnouncementsProvider";
import { FragmentFields } from "@scrile/api-provider/dist/api/types";
import useAuthUser from "@/hooks/useAuthUser";
import store from "@/store";
import { MUTATION_TYPES } from "@/store/const";
import { StateRoot } from "@/store/state";
import providers from "@/lib/api";

export function useAnnouncements() {
  const { isMember, isCreator } = useAuthUser();
  const loading = ref(false);
  const announcements = ref<Announcement[]>([]);
  const vm = getCurrentInstance();
  const hasUnreadAnnouncement = computed<StateRoot["hasUnreadAnnouncement"]>(() => {
    return (vm?.$store.state as StateRoot).hasUnreadAnnouncement;
  });

  const getAnnouncements = async (
    data: Partial<InputAnnouncementSearch> = {},
    fields?: FragmentFields<Announcement>
  ) => {
    try {
      loading.value = true;
      if (!isMember.value && !isCreator.value) return;
      announcements.value = await providers.AnnouncementsProvider.search(
        {
          data: {
            types: [
              AnnouncementTypeEnum.BOTH,
              isMember.value ? AnnouncementTypeEnum.MEMBER : AnnouncementTypeEnum.CREATOR,
            ],
            size: 999,
            sort: "ID,DESC",
            ...data,
          },
        },
        fields
      );
    } finally {
      loading.value = false;
    }
  };

  const getUnreadAnnouncement = async () => {
    const payload = !!(await providers.AnnouncementsProvider.getUnreadStats()).unreadCount;
    store.commit(MUTATION_TYPES.SET_UNREAD_ANNOUNCEMENTS, payload);
  };

  const readAnnouncement = async (announcementId: string) => {
    await providers.AnnouncementsProvider.markRead({ maxReadId: announcementId });
    await getUnreadAnnouncement();
  };

  return {
    loading,
    announcements,
    hasUnreadAnnouncement,
    getAnnouncements,
    getUnreadAnnouncement,
    readAnnouncement,
  };
}
